import {
  DomElementAlignment,
  DomTargetPosition,
  OdinIcon,
  OdinIconSize,
  Tooltip,
  useTestTag,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { RecordListMenuItemValue } from '../../../@types/record-list-menu';
import { dataTag } from '../../../utils/data-tags';
import type { DataGridSettingsMenuItem } from './data-grid-menu-utils';

interface PinnableMenuProps {
  items: Array<DataGridSettingsMenuItem>;
  onPinMenuItemClick?: (key: RecordListMenuItemValue, isPinned: boolean, addonId?: number) => void;
  isModalMenu?: boolean;
}

function PinnableMenu({ items, onPinMenuItemClick, isModalMenu = false }: PinnableMenuProps) {
  const { t } = useTranslation();
  const tagCreator = useTestTag('pinnable-menu');

  const createItems = (items: Array<DataGridSettingsMenuItem>) => {
    return items.map((menuItem, index) => {
      if (menuItem.isSeparator) {
        return (
          <li key={menuItem.key} className="py-1" hidden={menuItem.visible === false}>
            <hr className="border-gray-3" />
          </li>
        );
      }

      const menuItemStyles = clsx('relative flex items-center gap-2 group/menu-item', {
        'hover:bg-primary-4': !menuItem.isTitle,
        'hover:!bg-mono-1': menuItem.disabled,
        [menuItem.className || '']: isModalMenu,
      });

      const menuItemLinkStyles = clsx(
        'block w-full px-3 py-2 flex gap-2 items-center whitespace-nowrap cursor-pointer',
        {
          'text-gray-3 pointer-events-none !cursor-default': menuItem.disabled,
        }
      );

      return (
        <li
          key={`${menuItem.key}_${menuItem.label}_${index}`}
          className={menuItemStyles}
          hidden={menuItem.visible === false}
        >
          {menuItem.isTitle ? (
            <Tooltip
              description={menuItem.titleDescription ?? menuItem.label}
              key={`${menuItem.key}_${menuItem.label}_${index}`}
              wrapperClassName="group/pinnedmenu-button relative"
              tooltipAlignment={DomElementAlignment.BottomLeft}
              tooltipTargetPosition={DomTargetPosition.TopLeft}
            >
              <p
                className={'flex flex-1 items-center gap-2 whitespace-nowrap px-3 py-2 ' + menuItem.className}
                ref={tagCreator(dataTag(menuItem.label))}
              >
                {menuItem.label}
              </p>
            </Tooltip>
          ) : (
            <>
              <a
                target={menuItem.target}
                href={menuItem.url}
                className={menuItemLinkStyles}
                onClick={menuItem.onClick}
                ref={tagCreator(dataTag(menuItem.label))}
              >
                {menuItem.icon && (
                  <span>
                    <OdinIcon icon={menuItem.icon} size={OdinIconSize.ExtraSmall} />
                  </span>
                )}
                {menuItem.label}
              </a>
              <button
                className="mr-2 rounded-full p-1 hover:bg-primary-3"
                title={menuItem.isPinned ? t('unpin') : t('pin')}
                onClick={() =>
                  onPinMenuItemClick?.(
                    menuItem.key as RecordListMenuItemValue,
                    menuItem.isPinned ?? false,
                    menuItem.addonId
                  )
                }
                hidden={!!menuItem.isPinIconHidden}
              >
                <OdinIcon
                  icon="Pin"
                  size={OdinIconSize.ExtraSmall}
                  className={clsx({ 'fill-mono-0 text-mono-0': menuItem.isPinned })}
                />
              </button>
              {menuItem.subMenuItems && createMenu(menuItem.subMenuItems, true)}
            </>
          )}
        </li>
      );
    });
  };

  const menuStyles = `hidden py-1 text-sm rounded-md shadow-md bg-mono-1 absolute right-0 z-50`;

  const createMenu = (items: Array<DataGridSettingsMenuItem>, isSubMenu?: boolean) => {
    if (isSubMenu) {
      return (
        <div className={`${menuStyles} left-0 top-0 -translate-x-full group-hover/menu-item:block max-sm:max-w-min`}>
          <ul>{createItems(items)}</ul>
        </div>
      );
    }

    return <ul>{createItems(items)}</ul>;
  };

  return <>{createMenu(items)}</>;
}

export default PinnableMenu;
